import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export default function FAQNew() {
  const faqs = [
    {
      question: "Comment fonctionne le retour affectif ?",
      answer: "Le retour affectif est un rituel spirituel ancestral africain qui vise à rétablir le lien émotionnel entre deux personnes. Notre expert utilise des méthodes transmises de génération en génération pour éliminer les blocages et raviver les sentiments. Le rituel agit sur le plan spirituel et émotionnel pour faire revenir l'être aimé naturellement."
    },
    {
      question: "Peut-on récupérer son ex en 7 jours ?",
      answer: "Oui, dans la majorité des cas, les premiers résultats apparaissent en 7 jours. Le Maître garantit un retour dans ce délai grâce à des rituels puissants et personnalisés. Le délai peut varier selon la complexité de la situation, mais l'efficacité est prouvée avec plus de 15,000 couples réunis."
    },
    {
      question: "Le retour affectif fonctionne-t-il si mon ex est avec quelqu'un d'autre ?",
      answer: "Oui, le retour affectif fonctionne même si votre ex est en couple avec quelqu'un d'autre. Les rituels sont conçus pour briser les liens factices et raviver le véritable amour qui vous unit. De nombreux clients ont récupéré leur ex dans cette situation."
    },
    {
      question: "Combien coûte un retour affectif ?",
      answer: "La première consultation est gratuite et sans engagement. Les tarifs des rituels varient selon la complexité de votre situation. Des tarifs transparents sont proposés avec possibilité de facilités de paiement. Contactez-nous pour un devis personnalisé."
    },
    {
      question: "Le retour affectif est-il garanti ?",
      answer: "Nous garantissons un travail sérieux et un engagement total. Avec 30 ans d'expérience et un taux de réussite de 99%, les résultats sont prouvés. Chaque situation est évaluée honnêtement lors de la consultation gratuite pour déterminer les chances de réussite."
    },
    {
      question: "Les consultations sont-elles disponibles partout ?",
      answer: "Oui, des consultations à distance sont proposées pour toute la France et l'international. Le travail spirituel est tout aussi efficace à distance. Consultations disponibles par téléphone et WhatsApp 7j/7 de 8h à 23h30."
    },
    {
      question: "Comment savoir si mon ex va revenir ?",
      answer: "Lors de la première consultation gratuite, le don de voyance permet de percevoir l'avenir de votre relation. Vous saurez honnêtement si un retour est possible et dans quels délais. Cette précision est reconnue par plus de 15,000 clients satisfaits."
    },
    {
      question: "Quelle est la différence entre voyance et retour affectif ?",
      answer: "La voyance permet de percevoir l'avenir et d'obtenir des réponses sur votre situation. Le retour affectif est un rituel actif qui agit concrètement pour faire revenir l'être aimé. Les deux sont combinés : la voyance pour analyser votre situation, puis les rituels nécessaires."
    },
    {
      question: "Les rituels de retour affectif sont-ils de la magie noire ?",
      answer: "Non, les rituels sont basés sur la spiritualité africaine ancestrale et la magie blanche. Ils ne nuisent à personne et respectent le libre arbitre. L'objectif est de rétablir l'amour véritable, pas de manipuler ou forcer quelqu'un."
    },
    {
      question: "Combien de temps durent les résultats du retour affectif ?",
      answer: "Les résultats du retour affectif sont durables et définitifs. Une fois le lien rétabli, votre couple sera même plus fort qu'avant. Un rituel de protection est également effectué pour préserver votre relation des influences négatives futures."
    }
  ];

  const relatedArticles = [
    { title: 'Retour Affectif Rapide', href: '#services' },
    { title: 'Récupérer Son Ex en 7 Jours', href: '#services' },
    { title: 'Retour Affectif Garanti', href: '#temoignages' },
    { title: 'Faire Revenir Son Ex Qui Refuse', href: '#services' },
    { title: 'Témoignages : Mon Ex Est Revenu', href: '#temoignages' },
    { title: 'Tous les articles', href: '#services' }
  ];

  return (
    <section id="faq" className="py-12 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Questions Fréquentes
          </h2>
          <p className="text-lg text-gray-400">
            Réponses aux questions sur le retour affectif et récupérer son ex.
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4 mb-12">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-xl px-6 data-[state=open]:border-yellow-500/50 transition-all"
            >
              <AccordionTrigger className="text-left text-white hover:text-yellow-500 transition-colors py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-400 leading-relaxed pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Related Articles */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold text-white text-center mb-6">Articles sur le Retour Affectif :</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {relatedArticles.map((article, idx) => (
              <a
                key={idx}
                href={article.href}
                className="flex items-center gap-2 p-4 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 hover:border-yellow-500/40 rounded-lg transition-all group"
              >
                <ArrowRight className="h-4 w-4 text-yellow-500 group-hover:translate-x-1 transition-transform" />
                <span className="text-sm text-gray-300 group-hover:text-yellow-500 transition-colors">{article.title}</span>
              </a>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl">
          <h3 className="text-2xl font-bold text-white mb-4">Vous avez d'autres questions ?</h3>
          <p className="text-gray-400 mb-6">Maître Diamma est disponible pour répondre à toutes vos questions</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
              <a href="tel:+33745449641">Appelez-nous</a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
              <a href="#contact">Ou envoyez un message</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
